import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconNavigationArrowDiagonal } from "@/assets/icons/figma/IconNavigationArrowDiagonal";
import { IconNavigationArrowRepeat } from "@/assets/icons/figma/IconNavigationArrowRepeat";
import { useFormatPrice } from "@/common/hooks/intl.hooks";
import { SelectableCard } from "@/features/create-ad-packages/components/SelectableCard";
import { BoostPackage } from "@/features/create-ad-packages/contracts/create-ad-packages.contracts";
import { NO_BOOST_DAYS } from "@/features/create-ad-packages/hooks/usePackageCartSelection";
import { InfoLabel } from "@/ui/cva/InfoLabel";

type BoostCardProps = {
  /** A boost package, or `undefined` for the synthetic free "No boost" option. */
  pkg?: BoostPackage;
  selected?: boolean;
  onSelect?: () => void;
  showRecommendedBadge?: boolean;
};

export const BoostCard = ({
  pkg,
  selected,
  onSelect,
  showRecommendedBadge = true,
}: BoostCardProps) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { formatPrice } = useFormatPrice({ locale, place: "money" });

  const days = pkg?.days ?? NO_BOOST_DAYS;
  const isFree = !pkg;

  return (
    <SelectableCard
      name="create-ad-boost"
      selected={selected}
      onSelect={onSelect}
      title={
        isFree
          ? t("boost-ad:boost-ad-none", "Geen boost")
          : t("boost-ad:boost-title", {
              defaultValue: "{{days}}-daagse boost",
              days,
            })
      }
      trailing={
        isFree ? (
          <InfoLabel
            color="curious"
            size="small"
            corners="rounded"
            weight="bold"
          >
            {t("common:common-free", "Gratis")}
          </InfoLabel>
        ) : (
          <span className="font-title font-bold">
            {formatPrice({ amount: pkg.price, currency: pkg.currency })}
          </span>
        )
      }
    >
      {!isFree && (
        <div className="mt-3 flex flex-col gap-2 text-grey-500">
          <span className="flex items-center gap-1">
            <IconNavigationArrowRepeat className="size-4 shrink-0" />
            {t(`boost-ad:boost-ad-info-v2:${days === 1 ? "one" : "other"}`, {
              defaultValue: "Elke dag geboost voor {{days}} dagen",
              days,
              count: days,
            })}
          </span>
          {showRecommendedBadge && pkg.recommended && (
            <InfoLabel
              color="attention"
              size="small"
              corners="rounded"
              weight="bold"
              className="w-fit"
              leftIcon={
                <IconNavigationArrowDiagonal className="size-4 text-attention-900" />
              }
            >
              {t(
                "boost-ad:boost-ad-upsell",
                "Verkoop gemiddeld 20 dagen sneller"
              )}
            </InfoLabel>
          )}
        </div>
      )}
    </SelectableCard>
  );
};
